/** note - play screen config is now managed locally in src/app/screens/play-screen/playTabConfig.ts */

import { z } from 'zod'
import { ADVISOR_SUPPORTED_MODES, DEFAULT_ADVISOR_SUPPORTED_MODES } from '@ppwr/convex/players/constants'
import { tabNavConfigSchema } from './tabNavConfigSchema'
import { playScreenConfigSchema } from './playScreenConfigSchema'
import { loggerSeverityConfigSchema } from './loggerSeverityConfigSchema'
import { ConvexConfigSchema } from './convexConfigSchema'

const advisorSupportedModeSchema = z.enum(ADVISOR_SUPPORTED_MODES)

export const JSONFlagsSchema = z.object({
  test_json: z
    .object({
      name: z.string(),
      versionForTesting: z.string()
    })
    .default({
      name: 'default',
      versionForTesting: ''
    }),
  tab_nav_config: tabNavConfigSchema,
  play_tab_config: playScreenConfigSchema,
  logger_severity_config: loggerSeverityConfigSchema,
  maintenance_mode: z
    .object({
      enabled: z.boolean().default(false),
      title: z.string().default('Maintenance Mode'),
      message: z.string().default('The games are currently under maintenance. Please try again later.')
    })
    .default({}),
  maintenance_msg: z
    .object({
      text1: z.string(),
      text2: z.string()
    })
    .default({
      text1: '',
      text2: ''
    }),
  advisor_config: z
    .object({
      enabled: z.boolean().default(true),
      supportedModes: z.array(advisorSupportedModeSchema).default([...DEFAULT_ADVISOR_SUPPORTED_MODES])
    })
    .default({}),
  convex_config: ConvexConfigSchema
})

export const NumberFlagsSchema = z.object({
  // num: z.number().default(0)
})

export const BooleanFlagsSchema = z.object({
  streamlined_experience_test: z.boolean().default(false),
  is_home_welcome_message_enabled: z.boolean().default(true),
  is_new: z.boolean().default(false),
  is_learning_unlocked: z.boolean().default(false),
  is_teaching_tables_enabled: z.boolean().default(false),
  is_daily_tasks_enabled: z.boolean().default(true),
  is_daily_inspiration_enabled: z.boolean().default(true),
  is_daily_discussions_enabled: z.boolean().default(true),
  is_last_game_stats_enabled: z.boolean().default(true),
  is_gold_membership_enabled: z.boolean().default(false),
  is_community_enabled: z.boolean().default(true),
  is_avatar_timer_enabled: z.boolean().default(true)
})

export const StringFlagsSchema = z.object({
  experiment_id: z.string().default(''),
  experiment_variant: z.string().default(''),
  // LearnWorlds category the Learn tab lists (PP-4829); empty shows every course.
  courses_category: z.string().default('Mobile App')
})

export const RemoteConfigSchema = z.object({
  ...JSONFlagsSchema.shape,
  ...NumberFlagsSchema.shape,
  ...BooleanFlagsSchema.shape,
  ...StringFlagsSchema.shape
})

export type RemoteConfigSchemaType = ReturnType<typeof RemoteConfigSchema.parse>

// Same as Remote Config but with all json flags as strings
// This is because the firebase returns all json flags as strings
export type InputSchemaType = {
  [key in keyof z.infer<typeof RemoteConfigSchema>]: key extends keyof z.infer<typeof JSONFlagsSchema>
    ? ReturnType<typeof z.string>
    : (typeof RemoteConfigSchema)['shape'][key]
}

const InputShape = Object.entries(RemoteConfigSchema.shape).reduce<InputSchemaType>((acc, [_key, value]) => {
  const key = _key as keyof typeof RemoteConfigSchema.shape

  if (key in JSONFlagsSchema.shape) {
    // @ts-expect-error TS can't infer acc[key] is scoped to only json
    acc[key] = z.string().optional()
    return acc
  }
  // @ts-expect-error TS can't infer acc[key] is scoped to only non json
  acc[key] = value
  return acc
}, {} as InputSchemaType)

const InputSchema = z.object(InputShape)
export const defaultInputRemoteConfig = InputSchema.parse({})
